import type * as React from 'react'
import { useState } from 'react'

import { cn } from '../../lib/utils'

const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

function normalizeDigits(value: string): string {
  return value.replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
}

function decimalSeparator(locale: string): string {
  const parts: Intl.NumberFormatPart[] = new Intl.NumberFormat(locale).formatToParts(1.1)
  return parts.find((part: Intl.NumberFormatPart) => part.type === 'decimal')?.value ?? '.'
}

function resolveLocale(locale?: string): string {
  const candidate =
    locale || (typeof document !== 'undefined' ? document.documentElement.lang : '') || 'en-US'
  try {
    Intl.NumberFormat(candidate)
    return candidate
  } catch {
    return 'en-US'
  }
}

function normalizeMoney(value: string, locale: string): string {
  const normalized = normalizeDigits(value).replace(/٫/g, '.').replace(/٬/g, ',')
  const localeDecimal = decimalSeparator(locale)
  const separators: string[] = normalized.match(/[.,]/g) ?? []
  const decimal = separators.includes(localeDecimal) ? localeDecimal : separators.at(-1)
  let result = ''
  let hasDecimal = false

  for (const character of normalized) {
    if (/\d/.test(character)) result += character
    else if (character === decimal && !hasDecimal) {
      result += '.'
      hasDecimal = true
    }
  }
  return result
}

export type MoneyInputProps = Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'onBlur' | 'value'
> & {
  value: string
  onChange: (value: string) => void
  onBlur?: (value: string) => void
  locale?: string
  roundOnBlur?: number
  /**
   * Decimals shown at rest: a fixed count, or `'typed'` to add none and keep
   * every significant one the value holds (`111` stays `111`, `6500.0000`
   * reads `6,500`, `12.125` stays `12.125`).
   */
  displayFractionDigits?: number | 'typed'
  invalid?: boolean
}

/**
 * An amount field: the `Input` box (DESIGN.md §5 "Inputs and fields") with
 * the figure at the end of the field in both languages, tabular, and digits
 * normalised from Arabic-Indic as they are typed. Hover, ring, invalid and
 * disabled states match every other field.
 */
function MoneyInput({
  className,
  invalid,
  locale,
  onBlur,
  onChange,
  onFocus,
  roundOnBlur,
  displayFractionDigits,
  value,
  ...props
}: MoneyInputProps) {
  const resolvedLocale = resolveLocale(locale)
  const [focused, setFocused] = useState(false)
  const canFormatValue = /^-?\d+(?:\.\d+)?$/.test(value)
  // 'typed' adds no decimals and keeps every significant one the value has.
  const typed = displayFractionDigits === 'typed'
  const maxDigits = typed ? (value.split('.')[1]?.length ?? 0) : displayFractionDigits
  const displayValue =
    maxDigits !== undefined && !focused && canFormatValue
      ? new Intl.NumberFormat(resolvedLocale, {
          // Grouped at rest so a large amount reads at a glance; focus shows
          // the bare value, so typing never meets a separator.
          numberingSystem: 'latn',
          minimumFractionDigits: typed ? 0 : maxDigits,
          maximumFractionDigits: maxDigits,
        }).format(value as Intl.StringNumericLiteral)
      : value
  return (
    <input
      data-slot="money-input"
      aria-invalid={invalid || undefined}
      {...props}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={(event) => onChange(normalizeMoney(event.target.value, resolvedLocale))}
      onFocus={(event) => {
        setFocused(true)
        event.currentTarget.select()
        onFocus?.(event)
      }}
      onBlur={(event) => {
        const normalized = normalizeMoney(event.currentTarget.value, resolvedLocale)
        const rounded =
          roundOnBlur !== undefined && normalized !== '' && Number.isFinite(Number(normalized))
            ? Number(normalized).toFixed(roundOnBlur)
            : normalized
        onChange(rounded)
        setFocused(false)
        onBlur?.(rounded)
      }}
      className={cn(
        'h-control w-full min-w-0 rounded-sm border border-input bg-input-background px-2.5 text-end tabular-nums text-body text-foreground',
        'transition-ui placeholder:text-muted-foreground',
        'enabled:hover:border-foreground',
        'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'aria-invalid:border-danger aria-invalid:enabled:hover:border-danger',
        'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground',
        '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        className,
      )}
    />
  )
}

export { MoneyInput }
